import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
// Keep the worker as a raw asset; Babel adds server-only helper paths.
import pdfWorkerUrl from "!!file-loader?name=static/media/[name].[contenthash:8].[ext]!pdfjs-dist/legacy/build/pdf.worker.min.mjs";

export default function PdfPreview({ blob, name }) {
  const canvas = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let stopped = false;
    let task;
    setPdf(null); setPage(1); setError(""); setRendering(true);
    (async () => {
      const { getDocument, GlobalWorkerOptions } = await import("pdfjs-dist/legacy/build/pdf.mjs");
      if (stopped) return;
      GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      const bytes = await blob.arrayBuffer();
      if (stopped) return;
      task = getDocument({ data: new Uint8Array(bytes), isEvalSupported: false });
      const document = await task.promise;
      if (!stopped) setPdf(document);
    })().catch(() => {
      if (!stopped) { setError("PDF önizlemesi açılamadı. Asıl belgeyi İndir düğmesiyle açabilirsiniz."); setRendering(false); }
    });
    return () => { stopped = true; task?.destroy().catch(() => {}); };
  }, [blob]);

  useEffect(() => {
    if (!pdf) return;
    let stopped = false;
    let task;
    setRendering(true); setError("");
    (async () => {
      const documentPage = await pdf.getPage(page);
      if (stopped) return;
      const size = documentPage.getViewport({ scale: 1 });
      // Bound canvas memory even when an uploaded PDF declares an enormous page.
      const viewport = documentPage.getViewport({ scale: Math.min(1.5, 1000 / size.width, 1400 / size.height) });
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.current.width = Math.ceil(viewport.width * ratio);
      canvas.current.height = Math.ceil(viewport.height * ratio);
      task = documentPage.render({ canvasContext: canvas.current.getContext("2d"), viewport, transform: [ratio, 0, 0, ratio, 0, 0] });
      await task.promise;
    })().catch((e) => {
      if (!stopped && e.name !== "RenderingCancelledException") setError("PDF sayfası gösterilemedi. Asıl belgeyi indirebilirsiniz.");
    }).finally(() => { if (!stopped) setRendering(false); });
    return () => { stopped = true; task?.cancel(); };
  }, [pdf, page]);

  return <section aria-label={`${name} PDF önizlemesi`} aria-busy={rendering} className="min-w-0 space-y-2">
    {pdf && <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
      <Button variant="outline" disabled={rendering || page <= 1} onClick={() => setPage((old) => old - 1)}>Önceki Sayfa</Button>
      <span aria-live="polite">Sayfa {page} / {pdf.numPages}</span>
      <Button variant="outline" disabled={rendering || page >= pdf.numPages} onClick={() => setPage((old) => old + 1)}>Sonraki Sayfa</Button>
    </div>}
    {rendering && <p role="status" className="text-sm text-slate-500">PDF hazırlanıyor…</p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <div className="max-h-[60vh] overflow-auto rounded border bg-slate-100 p-2">
      <canvas ref={canvas} hidden={!!error || !pdf} role="img" aria-label={`${name}, sayfa ${page}`} className="mx-auto h-auto max-w-full" data-testid="company-document-pdf-canvas" />
    </div>
  </section>;
}
